import { useDispatch, useSelector } from "react-redux";
import {
  setCertificates,
  addOrUpdateCertificate,
  removeCertificate,
  updateCertificateFormField,
  setLoading,
  setSubmitting,
  setUploadModalOpen,
  setEditModalOpen,
  setCertificateToEdit,
  clearCertificateToEdit,
  resetCertificateForm,
} from "../../store/slices/qztray/qzTrayCertificateSlice";

import QzTrayCertificateService from "../../services/qztray/QzTrayCertificateService";
import { RootState } from "../../store/store";
import {
  QzTrayCertificate,
  QzTrayCertificateForm,
  QzTrayCertificateType,
} from "../../types/qztray/QzTrayCertificateTypes";

import useApiErrorHandler from "../useApiErrorHandler";
import useLoading from "../useLoading";
import useAlerts from "../useAlerts";
import useModals from "../useModals";

/**
 * 🖨️ Hook: useQzTrayCertificate
 *
 * Controla la gestión de certificados QZ Tray:
 * listar, subir, actualizar, eliminar y manejar modales.
 */
export const useQzTrayCertificate = () => {
  const dispatch = useDispatch();
  const {
    form,
    error,
    certificates,
    isLoading,
    isSubmitting,
    uploadModalOpen,
    editModalOpen, // ✅ nuevo
    certificateToEdit,
  } = useSelector((state: RootState) => state.qzTrayCertificate);

  const { handle } = useApiErrorHandler();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
  const { show: showAlert } = useAlerts();
  const { openModal } = useModals();

  // ========================================================
  // 📥 Obtener certificados
  // ========================================================
  const fetchCertificates = async () => {
    try {
      dispatch(setLoading(true));
      showLoadingIndicator();
      const response = await QzTrayCertificateService.getAllCertificates();
      console.log(response);
      const { statusCode, data } = response;
      if (statusCode === 200) dispatch(setCertificates(data));
    } catch (error: any) {
      handle(error);
    } finally {
      dispatch(setLoading(false));
      hideLoadingIndicator();
    }
  };

  // ========================================================
  // 📤 Subir nuevo certificado
  // ========================================================
  const uploadCertificate = async (
    file: File,
    type: QzTrayCertificateType
  ) => {
    try {
      dispatch(setSubmitting(true));
      showLoadingIndicator();

      const response = await QzTrayCertificateService.uploadCertificate(file, type);
      console.log(response);

      const { statusCode, data, type: alertType, title, message } = response;

      if (statusCode === 200) {
        dispatch(addOrUpdateCertificate(data));
        openModal(alertType, { title, message });
        closeUploadModal();
        fetchCertificates();
      } else {
        showAlert("error", "Error", "No se pudo subir el certificado.");
      }
    } catch (error: any) {
      handle(error);
    } finally {
      dispatch(setSubmitting(false));
      hideLoadingIndicator();
    }
  };

  // ========================================================
  // 🔐 Generar certificado automáticamente
  // ========================================================
  const generateCertificate = async (request: any) => {
    try {
      dispatch(setSubmitting(true));
      showLoadingIndicator();

      const response =
        await QzTrayCertificateService.generateCertificate(request);

      const { statusCode, type: alertType, title, message } = response;

      if (statusCode === 200) {
        openModal(alertType, { title, message });
        fetchCertificates();
      } else {
        showAlert(
          "error",
          "Error",
          "No se pudo generar el certificado."
        );
      }
    } catch (error: any) {
      handle(error);
    } finally {
      dispatch(setSubmitting(false));
      hideLoadingIndicator();
    }
  };

  // ========================================================
  // 📥 Descargar certificado
  // ========================================================
  const downloadCertificate = async (type: QzTrayCertificateType) => {
    try {
      dispatch(setSubmitting(true));
      showLoadingIndicator();

      const fileName = await QzTrayCertificateService.downloadCertificate(type);

      openModal("success", {
        title: "Descarga completada",
        message: `El archivo ${fileName} fue descargado correctamente.`,
      });
    } catch (error: any) {
      handle(error);
    } finally {
      dispatch(setSubmitting(false));
      hideLoadingIndicator();
    }
  };

  // ========================================================
  // ✏️ Actualizar certificado existente
  // ========================================================
  const updateCertificate = async (
    file: File,
    type: QzTrayCertificateType
  ) => {
    try {
      dispatch(setSubmitting(true));
      showLoadingIndicator();

      const response = await QzTrayCertificateService.uploadCertificate(file, type);
      const { statusCode, data, type: alertType, title, message } = response;

      if (statusCode === 200) {
        dispatch(addOrUpdateCertificate(data));
        openModal(alertType, { title, message });
        closeEditModal();
        fetchCertificates();
      } else {
        showAlert("error", "Error", "No se pudo actualizar el certificado.");
      }
    } catch (error: any) {
      handle(error);
    } finally {
      dispatch(setSubmitting(false));
      hideLoadingIndicator();
    }
  };

  // ========================================================
  // 🧹 Eliminar certificado
  // ========================================================
  const deleteCertificate = async (type: QzTrayCertificateType) => {
    try {
      dispatch(setSubmitting(true));
      showLoadingIndicator();

      const response = await QzTrayCertificateService.deleteCertificate(type);
      console.log(response);
      const { statusCode, type: alertType, title, message } = response;

      if (statusCode === 200) {
        dispatch(removeCertificate(type));
        openModal(alertType, { title, message });
      }
    } catch (error: any) {
      handle(error);
    } finally {
      dispatch(setSubmitting(false));
      hideLoadingIndicator();
    }
  };

  // ========================================================
  // 🧭 Control de campos y modales
  // ========================================================
  const updateField = <K extends keyof QzTrayCertificateForm>(
    field: K,
    value: QzTrayCertificateForm[K]
  ) => {
    dispatch(updateCertificateFormField({ field, value }));
  };

  const openUploadModal = () => dispatch(setUploadModalOpen(true));
  const closeUploadModal = () => dispatch(setUploadModalOpen(false));

  const openEditModal = () => dispatch(setEditModalOpen(true)); // ✅ nuevo
  const closeEditModal = () => dispatch(setEditModalOpen(false)); // ✅ nuevo

  const selectCertificateToEdit = (certificate: QzTrayCertificate) => {
    dispatch(setCertificateToEdit(certificate));
    openEditModal(); // ✅ abrir modal automáticamente
  };

  const clearCertificate = () => {
    dispatch(clearCertificateToEdit());
  };

  const clearGenerateCertificateForm = () => {
    dispatch(resetCertificateForm());
  };

  // ========================================================
  // 🧩 Retorno del hook
  // ========================================================
  return {
    form,
    error,
    certificates,
    isLoading,
    isSubmitting,
    uploadModalOpen,
    editModalOpen, // ✅ nuevo
    certificateToEdit,

    fetchCertificates,
    uploadCertificate,
    generateCertificate,
    downloadCertificate,
    updateCertificate, // ✅ nuevo
    deleteCertificate,
    updateField,

    openUploadModal,
    closeUploadModal,
    openEditModal, // ✅ nuevo
    closeEditModal, // ✅ nuevo
    selectCertificateToEdit,
    clearCertificate,
    clearGenerateCertificateForm
  };
};
